import * as React from 'react'
import {
  PROPOSAL_WORKS,
  type ProposalWork,
} from '@lib/enums/proposal-work.enum'
import { ProposalWordingEditor } from '@/components/ProposalWordingEditor'
import { cn } from '@/lib/utils'

/** What each set of wording is for, as the switch names it. */
const WORDING_LABELS: Record<ProposalWork, string> = {
  TREES: 'Trees',
  IRRIGATION: 'Irrigation',
  BOTH: 'Trees and irrigation',
}

const WORDING_BLURBS: Record<ProposalWork, string> = {
  TREES:
    'The standard language every tree proposal is built from. Preview shows it on a sample proposal, so you can read it the way a client will.',
  IRRIGATION:
    'The standard language for proposals that bid irrigation repairs alone. Preview shows it on a sample proposal with repairs.',
  BOTH: 'The standard language for proposals that bid trees and irrigation together, written as one document rather than the other two stacked.',
}

/**
 * The business's proposal wording, one set for each kind of proposal.
 *
 * A proposal says what it bids, so trees, irrigation, and the two together
 * each have their own wording; the switch picks which is being edited. An
 * editor stays mounted once opened, hidden rather than removed, so switching
 * to look at another set does not throw away edits not yet saved.
 */
export function ProposalWordingSettings() {
  const [work, setWork] = React.useState<ProposalWork>('TREES')
  const [opened, setOpened] = React.useState<ReadonlySet<ProposalWork>>(
    () => new Set<ProposalWork>(['TREES']),
  )

  function choose(next: ProposalWork): void {
    setWork(next)
    setOpened((current) => new Set(current).add(next))
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-secondary-text">Wording for</span>
        <div
          role="radiogroup"
          aria-label="Wording for"
          className="inline-flex rounded-md border border-default p-0.5"
        >
          {PROPOSAL_WORKS.map((entry) => (
            <button
              key={entry}
              type="button"
              role="radio"
              aria-checked={work === entry}
              onClick={() => {
                choose(entry)
              }}
              className={cn(
                'rounded-sm px-2.5 py-1 text-xs transition-colors',
                work === entry
                  ? 'bg-subtle font-medium text-primary-text'
                  : 'text-secondary-text hover:text-primary-text',
              )}
            >
              {WORDING_LABELS[entry]}
            </button>
          ))}
        </div>
      </div>
      {PROPOSAL_WORKS.filter((entry) => opened.has(entry)).map((entry) => (
        <div key={entry} hidden={entry !== work}>
          <ProposalWordingEditor
            work={entry}
            title={`Proposal wording · ${WORDING_LABELS[entry]}`}
            blurb={WORDING_BLURBS[entry]}
          />
        </div>
      ))}
    </div>
  )
}
